'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, Languages, X, Check } from 'lucide-react';
import { FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { BiLogoTiktok } from 'react-icons/bi';
import 'react-loading-skeleton/dist/skeleton.css';
import { AgentBadges } from '../components/ui/agent-badge';

const AgentCard = ({ agent, isLoading }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [notification, setNotification] = useState(null);
  const router = useRouter();

  // Create a URL-friendly slug from the agent's name
  const agentSlug = agent?.name?.toLowerCase().replace(/\s+/g, '-');

  // Check localStorage on component mount for saved preferences
  useEffect(() => {
    if (typeof window !== 'undefined' && agent) {
    }
  }, [agent]);

  // Show notification
  const showNotification = (message, isSuccess) => {
    setNotification({ message, isSuccess });

    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
    hover: {
      y: -10,
      boxShadow:
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      transition: { duration: 0.3 },
    },
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && agent) {
    }
  }, [agent]);

  if (isLoading) {
    return (
      <motion.div
        variants={cardVariants}
        className='w-[280px] h-[400px] rounded-xl overflow-hidden shadow-lg bg-white dark:bg-gray-800'
      >
        <div className='p-6 flex flex-col items-center'>
          <Skeleton circle width={150} height={150} className='mb-4' />
          <Skeleton width={180} height={24} className='mb-2' />
          <Skeleton width={140} height={20} className='mb-2' />
          <Skeleton width={120} height={20} className='mb-4' />
          <div className='flex gap-3 mt-2'>
            <Skeleton circle width={36} height={36} />
            <Skeleton circle width={36} height={36} />
            <Skeleton circle width={36} height={36} />
          </div>
        </div>
      </motion.div>
    );
  }

  const handleCardClick = () => {
    router.push(`/agents/${agentSlug}`);
  };

  return (
    <motion.div
      variants={cardVariants}
      initial='hidden'
      animate='visible'
      whileHover='hover'
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className='w-[280px] rounded-xl overflow-hidden shadow-lg bg-white dark:bg-gray-800 relative group'
    >
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute top-2 left-1/2 transform -translate-x-1/2 z-50 px-3 py-1 rounded-full text-sm ${
              notification.isSuccess
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            <div className='flex items-center gap-1'>
              {notification.isSuccess ? (
                <Check
                  size={12}
                  className='text-green-600 dark:text-green-400'
                />
              ) : (
                <X size={12} className='text-red-600 dark:text-red-400' />
              )}
              {notification.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div onClick={handleCardClick} className='group block cursor-pointer'>
        <div className='relative'>
          {/* Gold corner accent */}
          <div className='absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-xl z-10'></div>

          {/* Background gradient overlay */}
          <div className='absolute inset-0 bg-gradient-to-b from-transparent to-black/50 opacity-0 group-hover:opacity-100 transition-opacity z-10'></div>

          {/* Agent image */}
          <div className='relative w-full h-[220px] overflow-hidden'>
            {!imageLoaded && <Skeleton height={220} />}
            <Image
              src={agent.profile_image || '/placeholder.svg'}
              alt={agent.name}
              fill
              priority
              unoptimized
              className={`object-cover transition-transform duration-500 group-hover:scale-110 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
            />
          </div>

          {/* Agent Badges */}
          <div className='absolute bottom-4 left-4 z-20'>
            <AgentBadges agent={agent} />
          </div>

          {/* Specialization badge */}
          {agent.specialization && (
            <div className='absolute bottom-4 right-4 bg-primarycolor/90 text-white text-xs px-2 py-1 rounded-full z-20'>
              {agent.specialization}
            </div>
          )}

          {/* Languages badge */}
          {/* {agent.languages && agent.languages.length > 0 && (
            <div className='absolute bottom-4 left-4 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity'>
              <Languages size={12} />
              {agent.languages.slice(0, 2).join(', ')}
              {agent.languages.length > 2 && '...'}
            </div>
          )} */}
        </div>

        <div className='p-6 relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-md w-full max-w-sm'>
          {/* Gold corner accent */}
          <div className='absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37] rounded-br-xl pointer-events-none' />

          <h2 className='font-bold text-lg text-primarycolor dark:text-secondarycolor mb-1 transition-colors group-hover:text-[#D4AF37] break-words line-clamp-2'>
            {agent.name}
          </h2>

          <p className='text-gray-700 dark:text-gray-300 font-medium mb-1 break-words line-clamp-3 group-hover:text-primarycolor '>
            {agent.title}
          </p>

          <p className='text-gray-700 dark:text-gray-300 font-medium mb-1 break-words line-clamp-3'>
            {agent.designation}
          </p>

          {agent.languages && agent.languages.length > 0 && (
            <div>
              <p className='text-gray-700 dark:text-gray-300 font-medium mb-1 break-words line-clamp-3'>
                {agent.languages.slice(0, 2).join(', ')}
                {agent.languages.length > 2 && '...'}
              </p>
            </div>
          )}

          {/* Quick contact options that appear on hover */}
          <div className='mt-4 flex items-center justify-between'>
            <div className='flex gap-3'>
              {agent.socialLinks?.instagram && (
                <motion.a
                  href={agent.socialLinks.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className='w-9 h-9 rounded-full bg-pink-50 dark:bg-pink-900/20 flex items-center justify-center text-pink-500 hover:bg-[#E4405F] dark:hover:bg-[#E4405F] hover:text-white transition-colors'
                  aria-label='Instagram profile'
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaInstagram size={18} aria-hidden='true' focusable='false' />
                  <span className='sr-only'>Visit Instagram profile</span>
                </motion.a>
              )}

              {agent.socialLinks?.tiktok && (
                <motion.a
                  href={agent.socialLinks.tiktok}
                  target='_blank'
                  rel='noopener noreferrer'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className='p-2 rounded-full bg-blue-50 text-gray-500 dark:bg-pink-900/20 transition-all hover:bg-[#000000] dark:hover:bg-[#000000] hover:text-[#00f2ea]'
                  aria-label='Visit TikTok profile'
                  onClick={(e) => e.stopPropagation()}
                >
                  <BiLogoTiktok
                    size={18}
                    aria-hidden='true'
                    focusable='false'
                  />
                  <span className='sr-only'>Visit TikTok profile</span>
                </motion.a>
              )}

              {agent.socialLinks?.linkedin && (
                <motion.a
                  href={agent.socialLinks.linkedin}
                  target='_blank'
                  rel='noopener noreferrer'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className='w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-700 hover:bg-[#0077B5] dark:hover:bg-[#0077B5] dark:hover:bg-blue-900/30 hover:text-white transition-colors'
                  aria-label='Visit LinkedIn profile'
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaLinkedinIn
                    size={18}
                    aria-hidden='true'
                    focusable='false'
                  />
                  <span className='sr-only'>Visit Linkedin profile</span>
                </motion.a>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: isHovered ? 1 : 0,
                scale: isHovered ? 1 : 0.8,
                y: isHovered ? 0 : 10,
              }}
              transition={{ duration: 0.2 }}
              className='flex gap-2'
            >
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`tel:${agent.mobile}`, '_blank');
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className='w-9 h-9 rounded-full bg-primarycolor/10 flex items-center justify-center text-primarycolor'
                aria-label={`Call ${agent.name}`}
              >
                <Phone size={16} aria-hidden='true' focusable='false' />
                <span className='sr-only'> Agent Phone</span>
              </motion.button>

              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  e.stopPropagation();
                  window.open(`mailto:${agent.email}`, '_blank');
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className='w-9 h-9 rounded-full bg-primarycolor/10 flex items-center justify-center text-primarycolor'
                aria-label={`Email ${agent.name}`}
              >
                <Mail size={16} aria-hidden='true' focusable='false' />
                <span className='sr-only'> Agent Email</span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AgentCard;
